import type { SubscribePayload } from 'graphql-ws';
import {
	type CacheConfig,
	Observable,
	type RequestParameters,
	type SubscribeFunction,
	type Variables,
} from 'relay-runtime';

import { fg } from '@atlaskit/platform-feature-flags/fg';
import { getClient, getSinkError, type SinkError } from '@atlassian/graphql-subscription';

export type SubscribeOptions = {
	subscribeExtensions?: Record<string, string>;
	onSubscribeTrigger?: (payload: SubscribePayload) => void;
	/** Attach shape at subscribe time: opener vs join, and observer count on that channel. */
	onSubscribeError?: (
		payload: SubscribePayload & {
			requestId: string | null;
			isNewChannel: boolean;
			observerCount: number;
		},
	) => void;
};

// One upstream sub per channel (AFB-2118); fan out to local observers.
type LocalSink = {
	next: (value: unknown) => void;
	// requestId captured before the channel is dropped.
	error: (err: unknown, requestId: string | null) => void;
	complete?: () => void;
};

type Channel = {
	sinks: Set<LocalSink>;
	dispose: () => void;
	lastRequestId: string | null;
};

const channels = new Map<string, Channel>();

function stableStringify(value: unknown): string {
	if (value === null || typeof value !== 'object') {
		return JSON.stringify(value) ?? 'null';
	}
	if (Array.isArray(value)) {
		return `[${value.map(stableStringify).join(',')}]`;
	}
	const obj = value as Record<string, unknown>;
	const keys = Object.keys(obj).sort();
	return `{${keys.map((k) => `${JSON.stringify(k)}:${stableStringify(obj[k])}`).join(',')}}`;
}

function channelKey(payload: SubscribePayload): string {
	return stableStringify({
		pq: (payload.extensions as Record<string, unknown> | undefined)?.pq ?? null,
		operationName: payload.operationName ?? null,
		variables: payload.variables ?? null,
		extensions: payload.extensions ?? null,
	});
}

let cachedDefaultSubscribe: SubscribeFunction | undefined;

export function createSubscribeFn(subscribeOptions?: SubscribeOptions): SubscribeFunction;
export function createSubscribeFn(subscribeOptions?: SubscribeOptions): SubscribeFunction {
	if (typeof subscribeOptions === 'undefined' && typeof cachedDefaultSubscribe !== 'undefined') {
		return cachedDefaultSubscribe;
	}

	const fn: SubscribeFunction = (
		{ id, name: operationName }: RequestParameters,
		variables: Variables,
		cacheConfig: CacheConfig,
	) =>
		Observable.create((sink) => {
			const client = getClient();
			if (!client) {
				return sink.error(new Error('Unable to create subscription client'));
			}
			const replayConfig = cacheConfig?.metadata?.replay || undefined;

			const replayCommand =
				replayConfig !== null && typeof replayConfig === 'object' && 'command' in replayConfig
					? (replayConfig as Record<string, unknown>).command
					: undefined;
			const replayExtensions = replayCommand
				? { command: replayCommand }
				: replayConfig
					? { replay: replayConfig }
					: {};

			const payload: SubscribePayload = {
				query: '',
				operationName,
				variables,
				extensions: {
					pq: id,
					...(subscribeOptions?.subscribeExtensions || {}),
					...replayExtensions,
				},
			};

			subscribeOptions?.onSubscribeTrigger?.(payload);

			const key = channelKey(payload);
			// Keep one cleanup policy for the entire observer lifetime.
			const guardChannelOwnership = !fg('relay_subscription_cleanup_killswitch');

			const existingChannel = channels.get(key);
			let joinedChannel = existingChannel;
			// Capture before failure; the channel may reopen first.
			const isNewChannel = !existingChannel;
			const observerCount = (existingChannel?.sinks.size ?? 0) + 1;

			const localSink: LocalSink = {
				next: (value) => {
					// @ts-expect-error incompatible types between two libraries
					sink.next(value);
				},
				error: (err, requestId) => {
					sink.error(err as Error);
					subscribeOptions?.onSubscribeError?.({
						...payload,
						requestId,
						isNewChannel,
						observerCount,
					});
				},
				complete: () => sink.complete?.(),
			};

			if (!existingChannel) {
				const newChannel: Channel = {
					sinks: new Set([localSink]),
					dispose: () => {},
					lastRequestId: null,
				};
				joinedChannel = newChannel;
				let terminated = false;

				newChannel.dispose = client.subscribe(payload, {
					next: (value) => {
						// @ts-expect-error agg prop is missing on generic "extensions" type
						const aggRequestId = value?.extensions?.gateway?.request_id;
						if (aggRequestId) {
							newChannel.lastRequestId = aggRequestId;
						}
						for (const s of Array.from(newChannel.sinks)) {
							try {
								s.next(value);
							} catch {
								/* isolate sinks */
							}
						}
					},
					error: (sinkError: SinkError) => {
						terminated = true;
						const errorInfo = getSinkError(sinkError);
						const sinks = Array.from(newChannel.sinks);
						const { lastRequestId } = newChannel;
						if (!guardChannelOwnership || channels.get(key) === newChannel) {
							channels.delete(key);
						}
						newChannel.sinks.clear();
						for (const s of sinks) {
							try {
								s.error(errorInfo, lastRequestId);
							} catch {
								/* isolate sinks */
							}
						}
					},
					complete: () => {
						terminated = true;
						const sinks = Array.from(newChannel.sinks);
						if (!guardChannelOwnership || channels.get(key) === newChannel) {
							channels.delete(key);
						}
						newChannel.sinks.clear();
						for (const s of sinks) {
							try {
								s.complete?.();
							} catch {
								/* isolate sinks */
							}
						}
					},
				});

				// A synchronous terminal callback may already have opened a replacement.
				// Register only a live channel, once its real disposer is available.
				if (!guardChannelOwnership || !terminated) {
					channels.set(key, newChannel);
				}
			} else {
				existingChannel.sinks.add(localSink);
			}

			return () => {
				const c = guardChannelOwnership ? joinedChannel : channels.get(key);
				if (!c) {
					return;
				}
				const removed = c.sinks.delete(localSink);
				if (guardChannelOwnership && !removed) {
					return;
				}
				if (c.sinks.size === 0) {
					if (!guardChannelOwnership || channels.get(key) === c) {
						channels.delete(key);
					}
					try {
						c.dispose();
					} catch {
						/* upstream dispose errors must not break Relay teardown */
					}
				}
			};
		});

	if (typeof subscribeOptions === 'undefined') {
		cachedDefaultSubscribe = fn;
	}

	return fn;
}

export const subscribe: SubscribeFunction = createSubscribeFn();
